/**
 * Demo Mode
 *
 * Demo mode exists so the sales team can show what ENTER looks like without showing anyone's
 * private data (Jordan Kelley, 2026-09-01). That is a safety property, and NS-14 requires it be
 * guaranteed at the request boundary rather than by data coverage.
 *
 * The reason is that demo data is served per CALL SITE, not per page: stores branch on
 * `features.isDemo` and call a generator instead of the API. Coverage is partial and always will
 * be — `claim.store.ts` branches on 50 of its 78 calls, and several stores have none at all. An
 * unbranched call does not fail. It reaches the production API as the signed-in user and renders
 * that account's real data, silently, with nothing on screen to say so.
 *
 * Chasing every call site to completeness is unverifiable by inspection and re-breaks the next
 * time anyone adds a fetch. So the guarantee lives here instead: in demo mode the API layer
 * refuses to make a request at all, except for the few endpoints that identify the person
 * driving the demo. A screen with no generator fails visibly and harmlessly, and never succeeds.
 */

export const DEMO_MODE_KEY = 'demoModeEnabled';

/**
 * The demo QuickSight endpoint — a Lambda that mints an ANONYMOUS embed URL for a demo dashboard.
 *
 * John Russell, 2026-09-10: *"When its in demo mode, make this call: <this URL>. When its out of
 * demo mode, make a real api call."* It answers on the same path as the product endpoint and
 * returns the same `{ url }` shape, so only the host differs.
 *
 * **This is what keeps NS-14 intact for Reports.** The dashboard it points at is a demo dashboard
 * with fabricated data — `d375e72f-…`, embedded anonymously via
 * `GenerateEmbedUrlForAnonymousUserCommand`, which needs no QuickSight user and touches no
 * customer's analytics. The product endpoint uses REGISTERED-user embedding against the signed-in
 * account, which is precisely the thing demo mode must not render.
 *
 * A CONSTANT and not an env var, deliberately. The previous demo routing lived in
 * `REACT_APP_DEMO_API_URL`, and a missing value there fell back to the PRODUCTION API behind a
 * `console.warn` — demo mode silently serving real data because a build was configured wrong is
 * the exact failure NS-14 exists to prevent. A constant cannot be absent.
 */
export const DEMO_QUICKSIGHT_URL =
    'https://wtf42upnbcwru5huvzdd67gnru0rlasb.lambda-url.us-west-2.on.aws/provider/report/quicksight/url';

/** Origin half of {@link DEMO_QUICKSIGHT_URL}, used to scope the allowlist rule below. */
const DEMO_QUICKSIGHT_ORIGIN = 'https://wtf42upnbcwru5huvzdd67gnru0rlasb.lambda-url.us-west-2.on.aws';

/**
 * Thrown instead of making a request. Typed rather than a bare Error so a caller can tell "demo
 * mode declined this" apart from a network failure, and so the test for NS-14 can assert on it.
 */
export class DemoModeBlockedError extends Error {
    public readonly url: string;
    public readonly method: string;

    constructor(url: string, method: string) {
        super(
            `Demo mode blocked ${method} ${url}. This screen has no demo data, so the request ` +
                `was refused rather than served from production (NS-14).`,
        );
        this.name = 'DemoModeBlockedError';
        this.url = url;
        this.method = method;
    }
}

/**
 * The only requests demo mode lets through: signing in, and finding out who signed in.
 *
 * **These rules are wider than what the app calls, and the earlier version of this comment said
 * otherwise.** It claimed they "carry the identity of the staff member — never a patient", which
 * is false: `/auth` is a PREFIX, so it also admits `/auth/external_login_patient_details`,
 * `/auth/patient_registration` and the password-reset endpoints. Neither app calls any of them
 * (checked 2026-09-03), and under this feature's threat model — an ENTER employee on an ENTER
 * machine, a prospect watching the screen, nobody crafting requests — no operator is going to
 * invoke one by hand. So the prefix stays: narrowing it risks breaking a login path for a
 * exposure nobody can reach, and a broken demo is the likelier harm.
 *
 * It is recorded here rather than fixed because a comment that overstates a guarantee is worse
 * than a wide rule with an honest one. Two separate reviews quoted the old claim back as
 * verified.
 *
 * `/me` IS restricted to GET, deliberately: a demo must not be able to rename the real account it
 * is signed into. Note that permitting the request is not the same as permitting its response —
 * `/me` carries `accountAccess`, a roster of ENTER's customers, which is substituted at the store
 * boundary in `root.store.ts` rather than here (NS-14).
 *
 * The optional `/api` prefix covers the deployment where the app is served behind a proxy and
 * `basePath` is `/api` rather than an absolute host.
 */
const DEMO_ALLOWED_REQUESTS: ReadonlyArray<{
    path: RegExp;
    methods?: ReadonlyArray<string>;
    /** When set, the rule matches only this exact origin. */
    origin?: string;
}> = [
    // Logging in. Needs POST, so no method restriction.
    { path: /^(?:\/api)?\/oauth(?:\/|$)/ },
    { path: /^(?:\/api)?\/auth(?:\/|$)/ },
    // Who am I. Read-only.
    { path: /^(?:\/api)?\/me(?:\/|$)/, methods: ['GET'] },
    /**
     * The DEMO QuickSight endpoint, and only that one.
     *
     * Reports is the one screen whose demo data cannot be generated client-side — you cannot draw
     * a QuickSight dashboard with a faker. So demo mode calls the Lambda above, which returns an
     * anonymous embed URL for a dashboard full of fabricated data. Nothing real renders, so NS-14
     * holds rather than being excepted.
     *
     * **Scoped by ORIGIN, and that is load-bearing.** The demo Lambda answers on the SAME path as
     * the product endpoint (`/provider/report/quicksight/url`), so a path-only rule would permit
     * BOTH — and the product one returns a registered-user embed of the signed-in account's real
     * analytics. Permitting the demo endpoint must not silently permit its twin.
     *
     * This is not the host-pinned allowlist NS-14's graveyard rejects. That entry rejects pinning
     * hosts as a DEFENCE against an attacker who crafts requests, and there is no such attacker
     * here. This pins an origin to say WHICH OF TWO SERVICES we mean when their paths collide.
     */
    { path: /^\/provider\/report\/quicksight\/url$/, methods: ['GET'], origin: DEMO_QUICKSIGHT_ORIGIN },
];

/** The origin of an absolute request URL, or `undefined` when the URL is relative. */
function originOf(url: string): string | undefined {
    try {
        return new URL(url).origin;
    } catch {
        return undefined;
    }
}

/** The path of a request URL, whether it is absolute or already relative. */
function pathnameOf(url: string): string {
    try {
        return new URL(url).pathname;
    } catch {
        return url.split('?')[0];
    }
}

/**
 * Whether demo mode permits this request. Exported so the middleware and its test share one
 * definition of the allowlist rather than two that can drift.
 */
export function isDemoRequestAllowed(url: string, method: string = 'GET'): boolean {
    const path = pathnameOf(url);
    const origin = originOf(url);
    const verb = (method || 'GET').toUpperCase();
    return DEMO_ALLOWED_REQUESTS.some(
        rule =>
            rule.path.test(path) &&
            (!rule.methods || rule.methods.includes(verb)) &&
            // An origin-scoped rule matches only that origin. A relative URL has no origin, so it
            // can never satisfy one — which is correct: the demo endpoint is always absolute.
            (!rule.origin || rule.origin === origin),
    );
}

/**
 * Whether the demo flag is set in this browser.
 *
 * This is the stored INPUT, not the answer — read `isDemoModeActive` for whether demo mode is
 * actually in force. They agree today, and the split exists so that a future reason to decline
 * demo mode cannot leave the UI reporting an intention as a fact (NS-14).
 */
export function isDemoModeEnabled(): boolean {
    try {
        return localStorage.getItem(DEMO_MODE_KEY) === 'true';
    } catch (err) {
        // Private windows and blocked site-data both throw here. Failing to read the flag must
        // mean "not in demo mode", never "assume demo" — the safe default is the real app.
        console.error('Error reading demo mode from localStorage:', err);
        return false;
    }
}

export interface IDemoModeState {
    /** The flag as stored in this browser. */
    enabled: boolean;
    /** Whether demo mode is actually in force — what the UI must report. */
    active: boolean;
}

/**
 * The single place that decides whether demo mode is on. Everything that branches on demo mode
 * reads this, so the API boundary, the stores and the settings screen cannot disagree.
 */
/**
 * NOT the same question as "should stores use generated data".
 *
 * `features.isDemo` in the provider is true for demo mode AND for local-only dev AND under test,
 * because all three want generated data. This is true only for the demo-mode TOGGLE, because only
 * that case is a PHI-safety promise made to someone outside the company. A developer running with
 * `REACT_APP_LOCAL_ONLY=true` is not demoing to a prospect, and blocking their network would be
 * an obstacle rather than a protection.
 *
 * Keep them separate deliberately. Collapsing them would either strand local dev or weaken NS-14.
 */
export function resolveDemoMode(): IDemoModeState {
    const enabled = isDemoModeEnabled();
    return { enabled, active: enabled };
}

export function isDemoModeActive(): boolean {
    return resolveDemoMode().active;
}

/**
 * Throw unless demo mode permits this request.
 *
 * The middleware in `auth.service.ts` covers everything routed through the generated client —
 * 210 of roughly 216 call sites. It does NOT cover code that calls `fetch` or `XMLHttpRequest`
 * directly, and some does: the coder-rules dashboard has its own `fetch`, its own client-
 * credentials token and a hardcoded `https://api.enter.health` fallback, and document transfer
 * uses `XMLHttpRequest` against pre-signed URLs. Those bypass any middleware by construction.
 *
 * A boundary with a hole in it is not a boundary, and the Coders page is one of the five surfaces
 * the demo exists to show. So raw-request code calls this directly. Same predicate, same error,
 * one definition (NS-14).
 *
 * A no-op when demo mode is off, so it is safe to call unconditionally on a hot path.
 */
export function assertDemoAllowed(url: string, method: string = 'GET'): void {
    if (!isDemoModeActive()) {
        return;
    }
    if (isDemoRequestAllowed(url, method)) {
        return;
    }
    throw new DemoModeBlockedError(url, method);
}

/**
 * Keys that must survive the purge below. Everything else in browser storage goes.
 *
 * A KEEP-list, not a delete-list, and that polarity is the point: the next cache someone adds is
 * purged automatically, without anyone remembering to add it here. A delete-list would need
 * updating for every new key and would silently miss the one nobody thought of — which is exactly
 * how the coder-rules dashboard came to render production claim IDs from `localStorage` in demo
 * mode, with no request made and so nothing for the request boundary to block.
 *
 * Auth keys are stored as `${prefix}_${suffix}`, so they are matched by suffix.
 */
const DEMO_PRESERVED_KEY_SUFFIXES: ReadonlyArray<string> = [
    'access_token',
    'refresh_token',
    'account_id',
    'v2_auth',
    'code_verifier',
];

function isPreservedAcrossDemoToggle(key: string): boolean {
    return key === DEMO_MODE_KEY || DEMO_PRESERVED_KEY_SUFFIXES.some(suffix => key.endsWith(suffix));
}

/**
 * Drop everything the app has cached in this browser, keeping only the session.
 *
 * NS-14 is about what RENDERS, and a cache read is not a request: restored state paints before
 * any fetch is attempted, and a cache hit can suppress the fetch that would have been blocked, so
 * the failure is silent. Recent searches are the same shape — the operator's real patient-name and
 * MRN queries, replayed on focus with no network call at all.
 *
 * Called when demo mode is switched ON, immediately before the reload, so the app comes back with
 * nothing real left to restore.
 */
export function purgeNonAuthBrowserState(): void {
    for (const storage of [localStorage, sessionStorage]) {
        try {
            // `length`/`key(i)` rather than `Object.keys`: enumerating a Storage as own properties
            // happens to work in browsers but is a legacy-platform-object quirk, not the API.
            // Collect first, then delete — removing during iteration shifts the indices.
            const doomed: string[] = [];
            for (let i = 0; i < storage.length; i += 1) {
                const key = storage.key(i);
                if (key !== null && !isPreservedAcrossDemoToggle(key)) {
                    doomed.push(key);
                }
            }
            doomed.forEach(key => storage.removeItem(key));
        } catch (err) {
            // Storage can be unavailable or partially blocked. Failing to purge must not leave the
            // caller believing it succeeded, so this is logged rather than swallowed silently.
            console.error('Demo mode: could not purge cached browser state:', err);
        }
    }
}

export function enableDemoMode(): void {
    try {
        localStorage.setItem(DEMO_MODE_KEY, 'true');
        // Before the reload, not after: anything cached from normal use would otherwise be
        // restored on mount and rendered (NS-14).
        purgeNonAuthBrowserState();
        // A reload is required, not cosmetic: the API configuration and the stores both read demo
        // state once, at construction.
        window.location.reload();
    } catch (err) {
        console.error('Error enabling demo mode:', err);
    }
}

export function disableDemoMode(): void {
    try {
        localStorage.removeItem(DEMO_MODE_KEY);
        // Symmetric: the generated fixtures cached during the demo are not real data, but leaving
        // them for the next session would show invented claims inside the real app.
        purgeNonAuthBrowserState();
        window.location.reload();
    } catch (err) {
        console.error('Error disabling demo mode:', err);
    }
}

export function toggleDemoMode(): void {
    if (isDemoModeEnabled()) {
        disableDemoMode();
    } else {
        enableDemoMode();
    }
}
